import React from 'react';
import {
  Button,
  Icon,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Pagination,
  type SxProps,
  type Theme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ClearFilters, Loader, SearchIconButton } from '../elements';
import { LoaderSize } from '../constants/constants';
import { notifications, extractApiErrorMessage } from '../utils';
import { HealthSystemDomainFarmsResultFarmViewModel } from '../api/HealthSystemApi';
import { ApiService } from '../hooks';
import { tokens } from '../theme/tokens';
import { V4_DIALOG_MD, V4_OUTLINED_DEFAULT } from '../theme/v4Compat';

interface IProps {
  farm: Pick<HealthSystemDomainFarmsResultFarmViewModel, 'number' | 'name'> | null;
  onChange: (farm: HealthSystemDomainFarmsResultFarmViewModel | null) => void;
  disableAny?: boolean;
  disabled?: boolean;
  /** Extra styles for the trigger button. */
  sx?: SxProps<Theme>;
  excludeIds?: string[];
}

export interface FarmSelectModalHandle {
  openModal: () => void;
}

const FarmSelectModal = React.forwardRef<FarmSelectModalHandle, IProps>(
  ({ farm, onChange, disableAny, sx, disabled, excludeIds }, ref) => {
    const { t } = useTranslation();

    const [farms, setFarms] = React.useState<HealthSystemDomainFarmsResultFarmViewModel[]>([]);
    const [isBusy, setIsBusy] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const [open, setOpen] = React.useState(false);

    const [text, setText] = React.useState('');
    const [searchText, setSearchText] = React.useState('');

    const apiService = new ApiService();

    React.useImperativeHandle(ref, () => ({
      openModal() {
        setOpen(true);
      }
    }));

    // pagination
    const [page, setPage] = React.useState(1);
    const [total, setTotal] = React.useState(0);

    const handleChangePage = (_event: unknown, newPage: number) => {
      setPage(newPage);
      setLoaded(false);
    };
    // * pagination

    const farmsDisplayCount = 5;

    const loadFarms = async () => {
      setIsBusy(true);

      apiService.api.farms
        .farmsGetFarms({ Page: page, Size: farmsDisplayCount, SearchText: searchText })
        .then((result) => {
          setFarms(result.data.data);
          setTotal(result.data.total);
          setLoaded(true);
        })
        .catch((e: unknown) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    };

    const handleSelectorClick = () => {
      setOpen(true);
    };

    React.useEffect(() => {
      if (open && !loaded) {
        loadFarms();
      }
    }, [open, loaded]);

    React.useEffect(() => {
      if (!farm) {
        setTextAny();
      } else {
        setTextByFarm(farm);
      }
    }, [farm]);

    const handleClose = () => {
      setOpen(false);
    };

    const setTextByFarm = (farm: Pick<HealthSystemDomainFarmsResultFarmViewModel, 'number' | 'name'>) => {
      setText(`${farm.number} ${farm.name}`);
    };

    const setTextAny = () => {
      setText(t('f-farm-any'));
    };

    const handleFarmSelectClick = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
      setTextByFarm(farm);
      setOpen(false);
      onChange(farm);
    };

    const handleSelectAny = () => {
      setOpen(false);
      setTextAny();
      onChange(null);
    };

    const clearFilters = () => {
      setSearchText('');
    };

    const renderFiltersSection = () => {
      return (
        <div className="flex flex-col justify-center">
          <div className="box-border flex flex-wrap items-center justify-center pb-[5px] pl-[10px]">
            <div>
              <TextField
                label={t('f-com-search-farm')}
                variant="outlined"
                size="small"
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div>
              <ClearFilters onClick={clearFilters} />
            </div>

            <div>
              <SearchIconButton onClick={() => setLoaded(false)} />
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
        <Button
          size="medium"
          variant="outlined"
          color="inherit"
          sx={[V4_OUTLINED_DEFAULT, ...(Array.isArray(sx) ? sx : [sx])]}
          disabled={disabled}
          fullWidth
          endIcon={<Icon>{'home_work'}</Icon>}
          onClick={handleSelectorClick}
        >
          {text}
        </Button>

        <Dialog onClose={handleClose} aria-labelledby="farm-select-title" maxWidth="md" sx={V4_DIALOG_MD} open={open}>
          <DialogTitle id="farm-select-title" sx={{ background: tokens.sidebar }}>
            {t('f-farm-select-modal')}
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {renderFiltersSection()}

            <Divider />

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

            {loaded && farms && (
              <List dense>
                {farms
                  .filter((farm) => !excludeIds?.includes(farm.id))
                  .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                  .map((farm) => (
                    <ListItemButton onClick={() => handleFarmSelectClick(farm)} key={farm.id}>
                      <ListItemAvatar>
                        <Avatar>{farm?.name?.[0]?.toUpperCase() || '-'}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={farm.name} secondary={farm.number} />
                    </ListItemButton>
                  ))}

                <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                  <Pagination
                    count={Math.ceil(total / farmsDisplayCount)}
                    size="small"
                    color="primary"
                    showFirstButton
                    showLastButton
                    page={page}
                    onChange={handleChangePage}
                  />
                </ListItem>
              </List>
            )}
          </DialogContent>

          <DialogActions sx={{ background: tokens.sidebar }}>
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>

            <Button onClick={handleSelectAny} color="primary" disabled={disableAny} variant="outlined">
              {t('f-farm-any')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default FarmSelectModal;
